import { CancelToken } from "axios";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  IOption,
  PageHeader,
  Search,
  Head,
  PerformerAppPath,
  PerformerAppSmartBanner,
} from "component-library";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { facetGroupCode } from "shared/config/common";
import { navigator } from "shared/services/navigator";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import { PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams } from "models/api";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";
import applicationMap from "services/applicationMap";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IJobSender,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";
import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";

import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import { IJobsPageInjectedProps, IJobsPageMatchParams } from "../JobsPage";

export interface IAllPaidPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IAllPaidPageStoreProps {
  texts: IAllPaidPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    cancellationToken?: CancelToken
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
}

export interface IAllPaidPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IAllPaidPageProps
  extends IAllPaidPageStoreProps,
    IJobsPageInjectedProps,
    RouteComponentProps<IJobsPageMatchParams>,
    IAllPaidPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags {}

export interface IAllPaidPageState {
  selectedItems: JobTableItemUuid[];
}

export const INPUT_NAMES = {
  sortingType: "sortingType",
};

export class AllPaidPage extends React.Component<
  IAllPaidPageProps,
  IAllPaidPageState
> {
  public static pageSize: number = 50;
  public readonly state: Readonly<IAllPaidPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const {
      filterValues,
      paginatedItems,
      isLoading,
      commonTexts,
      texts,
      renderJobsFilterControl,
      isPerformer,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.AllPaid} />
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText="" />
          }
        />
        {renderJobsFilterControl()}
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={AllPaidPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={applicationMap.links.jobsAllPaid()}
              isPerformer={isPerformer}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div className="g-col-lg-6">
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
          />
        </div>
        <div className="g-col-lg-3 g-hidden-sm" />
        {this.renderSorter()}
      </div>
    );
  };

  private renderSorter() {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-col-lg-3 g-col-sm-12">
        <DropdownInput<string>
          id="paidPageSorting"
          name={INPUT_NAMES.sortingType}
          variant="select"
          options={commonTexts.sortingOptions}
          selected={commonTexts.sortingOptions.find(
            (option) => option.value === filterValues[FilterType.SortingType]
          )}
          value={filterValues[FilterType.SortingType]}
          optionSelected={this.handleSortingTypeChange}
        />
      </div>
    );
  }

  private handleUpdatePageNumber = (page: number) => {
    navigator.setQuery({ page });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    this.handleSearch(undefined);
  };

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private handleRefresh = () => {
    this.props.changeFilters({ [FilterType.PageNumber]: 1 });
    this.props.refreshCounters();
  };

  private initSearch() {
    const searchString = this.props.filterValues[FilterType.TextFilter];
    this.searchRef.current?.setState({ searchString: searchString ?? "" });
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.allPaid(this.props.texts.pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IAllPaidPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(commonTranslations, PageTitleKey.JobsFeedAllPaid),
    },
    isPerformer: userType === AccountType.Performer,
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
  };
};

const mapDispatchToProps: MapDispatchToProps<IAllPaidPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.AllPaid,
  filterContractTypes(facetGroups) {
    return facetGroups.filter(
      (facetGroup) => facetGroup.code !== facetGroupCode.contractType.lowPay
    );
  },
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IAllPaidPageProps>(withJobsFilterOptions)
)(AllPaidPage);
